import React from "react";
import { useApplication } from "../ApplicationProvider";
import { FileUpload } from "../FileUpload";
import { phonePlaceholderFor } from "../../validation";
import { PhoneInput } from "../PhoneInput";

export function Step4Insurance({ errors }: { errors: Record<string, string> }) {
  const { data, update } = useApplication();
  // Round 24 — see Step1Program: the format hint follows addressType.
  const phonePlaceholder = phonePlaceholderFor(data.step2.addressType);
  const set = <K extends keyof typeof data.step4>(key: K, value: (typeof data.step4)[K]) =>
    update((d) => {
      d.step4[key] = value as never;
    });

  return (
    <section className="space-y-6">
      <div className="section-card">
        <h2 className="text-xl font-semibold mb-1">Health Insurance</h2>
        <p className="text-sm text-slate-600 mb-4">
          Provide details of the health insurance policy that will cover the applicant during the session.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="field-label">Carrier name</label>
            <input className="field-input" value={data.step4.carrierName} onChange={(e) => set("carrierName", e.target.value)} />
            {errors["carrierName"] && <p className="field-error">{errors["carrierName"]}</p>}
          </div>
          <div>
            <label className="field-label">Policy / member ID</label>
            <input className="field-input" value={data.step4.policyId} onChange={(e) => set("policyId", e.target.value)} />
            {errors["policyId"] && <p className="field-error">{errors["policyId"]}</p>}
          </div>
          <div>
            <label className="field-label">Group number</label>
            <input className="field-input" value={data.step4.groupNumber} onChange={(e) => set("groupNumber", e.target.value)} />
          </div>
          <div>
            <label className="field-label">Claims phone number</label>
            <PhoneInput
              placeholder={phonePlaceholder}
              addressType={data.step2.addressType}
              value={data.step4.claimsPhone}
              onChange={(v) => set("claimsPhone", v)}
            />
            {errors["claimsPhone"] && <p className="field-error">{errors["claimsPhone"]}</p>}
          </div>
          <div>
            <label className="field-label">Policyholder name</label>
            <input className="field-input" value={data.step4.policyholderName} onChange={(e) => set("policyholderName", e.target.value)} />
            {errors["policyholderName"] && <p className="field-error">{errors["policyholderName"]}</p>}
          </div>
          <div>
            <label className="field-label">Policyholder relationship to applicant</label>
            <input className="field-input" value={data.step4.policyholderRelationship} onChange={(e) => set("policyholderRelationship", e.target.value)} />
            {errors["policyholderRelationship"] && <p className="field-error">{errors["policyholderRelationship"]}</p>}
          </div>
          <div>
            <label className="field-label">Policyholder DOB</label>
            <input type="date" className="field-input" value={data.step4.policyholderDob} onChange={(e) => set("policyholderDob", e.target.value)} />
            {errors["policyholderDob"] && <p className="field-error">{errors["policyholderDob"]}</p>}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <FileUpload
            fieldKey="insurance-card-front"
            label="Insurance card — front"
            helper="Upload a clear image of the front of the insurance card."
            accept=".jpg,.jpeg,.png,.pdf"
            required
          />
          <FileUpload
            fieldKey="insurance-card-back"
            label="Insurance card — back"
            helper="Upload a clear image of the back of the insurance card."
            accept=".jpg,.jpeg,.png,.pdf"
            required
          />
        </div>
      </div>
    </section>
  );
}
